import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, ArrowRight, Phone, MapPin } from 'lucide-react';
import { services, cities } from '../data/seoData';

export default function ServicePage() {
  const { serviceSlug } = useParams();
  
  const service = services[serviceSlug];
  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Leistung nicht gefunden</h1>
          <Link to="/leistungen"><Button>Alle Leistungen</Button></Link>
        </div>
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": "FarbWelt Maler Express Service UG (Haftungsbeschränkt)",
      "url": "https://www.farbwelt-service.de"
    },
    "areaServed": Object.values(cities).map(c => ({ "@type": "City", "name": c.name }))
  };

  return (
    <>
      <Helmet>
        <title>{service.name} | FarbWelt Service - Professionelle Malerarbeiten</title>
        <meta name="description" content={`${service.description} ✓ Faire Preise ✓ Termingerecht ✓ Qualitätsarbeit. Jetzt Termin in Dormagen, Düsseldorf, Köln & Umgebung buchen!`} />
        <meta name="keywords" content={service.keywords.join(', ')} />
        <link rel="canonical" href={`https://www.farbwelt-service.de/leistungen/${service.slug}`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2E2A88] via-[#1e1a5e] to-[#9B1B30] text-white py-20">
          <div className="container mx-auto px-4">
            <nav className="text-sm mb-6 text-white/80">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/leistungen" className="hover:text-white">Leistungen</Link>
              <span className="mx-2">/</span>
              <span>{service.name}</span>
            </nav>
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold font-barlow mb-6">
                {service.name}
              </h1>
              <p className="text-xl text-white/90 mb-8">
                {service.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/termin">
                  <Button size="lg" className="bg-[#9B1B30] hover:bg-[#7a1525]">
                    <Phone className="w-5 h-5 mr-2" />
                    Kostenlos beraten lassen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold font-barlow mb-8 text-center">
              Ihre Vorteile bei FarbWelt Service
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.benefits.map((benefit, idx) => (
                <Card key={idx} className="text-center">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="font-medium">{benefit}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Detailed Description */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto prose prose-lg">
              <h2 className="text-3xl font-bold font-barlow mb-6">
                {service.name} vom Fachbetrieb
              </h2>
              <p>
                Bei FarbWelt Service erhalten Sie professionelle {service.name.toLowerCase()} 
                von erfahrenen Malern. Wir arbeiten in Dormagen, Düsseldorf, Köln, Leverkusen, 
                Monheim am Rhein und allen umliegenden Stadtteilen.
              </p>
              <p>
                Unser Team verwendet ausschließlich hochwertige Materialien namhafter Hersteller 
                und legt größten Wert auf saubere, präzise Arbeit. Nach Abschluss der Arbeiten 
                hinterlassen wir Ihre Räume besenrein.
              </p>
              
              <h3>Unser Ablauf</h3>
              <ol>
                <li><strong>Kostenlose Besichtigung:</strong> Wir kommen zu Ihnen und besprechen Ihr Projekt vor Ort</li>
                <li><strong>Detailliertes Angebot:</strong> Sie erhalten ein transparentes Festpreisangebot</li>
                <li><strong>Termingerechte Ausführung:</strong> Wir führen die Arbeiten zum vereinbarten Termin durch</li>
                <li><strong>Abnahme:</strong> Gemeinsame Abnahme und Zufriedenheitsgarantie</li>
              </ol>

              <h3>Häufig gestellte Fragen zu {service.name}</h3>
              <div className="space-y-4 not-prose">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-bold mb-2">Was kostet {service.name.toLowerCase()}?</h4>
                  <p className="text-gray-600">Die Kosten hängen von der Größe und dem Zustand der Flächen ab. Wir erstellen Ihnen gerne ein kostenloses Angebot nach einer Vor-Ort-Besichtigung.</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-bold mb-2">Wie lange dauern die Arbeiten?</h4>
                  <p className="text-gray-600">Die Dauer hängt vom Umfang des Projekts ab. Bei der Besichtigung geben wir Ihnen eine realistische Zeitschätzung.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold font-barlow mb-8 text-center">
              {service.name} in Ihrer Stadt
            </h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.values(cities).map((city) => (
                <Link
                  key={city.slug}
                  to={`/maler-${city.slug}`}
                  className="p-4 bg-white border rounded-lg hover:border-[#9B1B30] hover:shadow-md transition-all text-center"
                >
                  <MapPin className="w-5 h-5 text-[#9B1B30] mx-auto mb-2" />
                  <span className="font-medium">{city.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#2E2A88] text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold font-barlow mb-4">
              Bereit für professionelle {service.name}?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Buchen Sie jetzt Ihren kostenlosen Besichtigungstermin.
            </p>
            <Link to="/termin">
              <Button size="lg" className="bg-[#9B1B30] hover:bg-[#7a1525] px-12">
                Jetzt Termin buchen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Other Services */}
        <section className="py-12 border-t">
          <div className="container mx-auto px-4">
            <h3 className="text-lg font-bold mb-4">Weitere Leistungen:</h3>
            <div className="flex flex-wrap gap-4">
              {Object.values(services).filter(s => s.slug !== service.slug).map((s) => (
                <Link
                  key={s.slug}
                  to={`/leistungen/${s.slug}`}
                  className="text-[#9B1B30] hover:underline"
                >
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
